/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars'
import CN from 'classnames'
import { useGetOrdersExportCSV } from 'framework/api/methods'

import { CheckBox } from 'components/atoms'
import { Button } from 'components/atoms/Button'
import { MultiSelect } from 'components/atoms/MultiSelect'
import { SimpleSelect } from 'components/atoms/Select'
import { capitalizeAll } from 'utils/capitalizeAll'
import {
  downloadFile,
  getFilenameFromContentDisposition,
} from 'utils/downloadFile'
import { formatPartnerOrderDateBound } from 'utils/partnerOrderDateBound'

import { ExportToastBody } from './ExportToastBody'

import './AdvanceSearchModal.scss'

/**
 * Client-side mirror of the backend 6-month export limit
 * (CsvReportHelper.IsWithinMaxMonths, BDL20-2286). Validating here lets the
 * modal stay open and flag the date range instead of failing after close.
 */
const MAX_REPORT_RANGE_MONTHS = 6

const DATE_RANGE_ERROR_MESSAGE = `The report date range cannot exceed ${MAX_REPORT_RANGE_MONTHS} months. Narrow "Select Date" in this filter to ${MAX_REPORT_RANGE_MONTHS} months or less, then export again.`

function isWithinMaxMonths(from: any, to: any): boolean {
  const start = new Date(from)
  const end = new Date(to)
  /** A missing/unparsable bound means there is no restriction to enforce (matches backend). */
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return true
  }
  /** Match C# DateTime.AddMonths: an overflowing day clamps to the target month's last day. */
  const limit = new Date(start)
  const dayOfMonth = limit.getDate()
  limit.setDate(1)
  limit.setMonth(limit.getMonth() + MAX_REPORT_RANGE_MONTHS)
  const lastDayOfTargetMonth = new Date(
    limit.getFullYear(),
    limit.getMonth() + 1,
    0,
  ).getDate()
  limit.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth))
  return limit >= end
}

/** Read a (possibly Blob) axios error body into a human-readable message. */
async function readBlobError(error: any): Promise<string> {
  const data = error?.response?.data
  try {
    if (data instanceof Blob) {
      const text = await data.text()
      try {
        const parsed = JSON.parse(text)
        return parsed?.Message || parsed?.message || text || 'Export failed.'
      } catch {
        return text || 'Export failed.'
      }
    }
    return data?.Message || data?.message || error?.message || 'Export failed.'
  } catch {
    return error?.message || 'Export failed.'
  }
}

export interface AdvanceSearchModalProps {
  isActive?: boolean | undefined
  className?: string | undefined
  isCustomStyles?: boolean | undefined
  onCloseClick?: () => void | undefined
  onOverlayClick?: () => void | undefined
  partnerList?: any
  setPartnerOrderVariables?: any
  partnerOrderVariables?: any
  searchKeyword?: any
  startDateValue?: any
  endDateValue?: any
  setPageNumber?: any
  orderTypeList?: any
}

export const AdvanceSearchModal: FC<AdvanceSearchModalProps> = ({
  className,
  isActive,
  isCustomStyles,
  onCloseClick,
  onOverlayClick,
  partnerList,
  setPartnerOrderVariables,
  partnerOrderVariables,
  searchKeyword,
  startDateValue,
  endDateValue,
  setPageNumber,
  orderTypeList,
}: AdvanceSearchModalProps) => {
  const list = [
    {
      id: 1,
      label: 'All',
      value: 'ALL',
      disabled: false,
    },
    {
      id: 2,
      label: 'Received',
      value: 'RECEIVED',
      disabled: false,
    },
    {
      id: 3,
      label: 'Back Order',
      value: 'BACKORDER',
      disabled: false,
    },
    {
      id: 4,
      label: 'Confirmed',
      value: 'CONFIRMED',
      disabled: false,
    },
    {
      id: 5,
      label: 'Open',
      value: 'OPEN',
      disabled: false,
    },
    {
      id: 6,
      label: 'To Be Consigned',
      value: 'TOBECONSIGNED',
      disabled: false,
    },
    {
      id: 7,
      label: 'Closed',
      value: 'CLOSED',
      disabled: false,
    },
    {
      id: 8,
      label: 'Partial-consigned',
      value: 'PARTIAL-CONSIGNED',
      disabled: false,
    },
    {
      id: 9,
      label: 'Failed',
      value: 'FAILED',
      disabled: false,
    },
    {
      id: 10,
      label: 'Cancelled',
      value: 'CANCELLED',
      disabled: false,
    },
    {
      id: 11,
      label: 'Exception',
      value: 'EXCEPTION',
      disabled: false,
    },
    {
      id: 12,
      label: 'Onhold',
      value: 'ONHOLD',
      disabled: false,
    },
  ]

  const exportToastIdRef = useRef<any>(null)

  const [selectedPartner, setSelectedPartner] = useState<any>(
    partnerOrderVariables.partner ||
      partnerList.filter((p: any) =>
        partnerOrderVariables.partnerCodes?.includes(p.label.split(' - ')[0]),
      ) ||
      '',
  )

  const [statusList, setStatusList] = useState(list)
  const [selectedStatus, setSelectedStatus] = useState<any>()
  const [startDate, setStartDate] = useState<any>(partnerOrderVariables.from)
  const [endDate, setEndDate] = useState<any>(partnerOrderVariables.to)
  const [isInternational, setIsInternational] = useState<any>(
    partnerOrderVariables.isInternational,
  )
  /** Inline validation for the Export CSV 6-month range limit (BDL20-2286). */
  const [dateRangeError, setDateRangeError] = useState<string | null>(null)

  const [selectedOrderType, setSelectedOrderType] = useState<any>(
    partnerOrderVariables.orderType ||
      orderTypeList.filter((p: any) =>
        partnerOrderVariables.partnerOrderTypes?.includes(p.label),
      ) ||
      orderTypeList[0],
  )
  const [isOrderTypeVisible, setIsOrderTypeVisible] = useState<boolean>()

  /**
   * Order type only applies to 5-digit partner codes, so changing the partner selection clears it.
   *
   * BDL20-3126: skip the very first (mount) pass. It used to clear the order types that had just been
   * hydrated from partnerOrderVariables.partnerOrderTypes — i.e. the filter the user chose on the
   * Dashboard before clicking a tile. The grid kept that filter (partnerOrderVariables is untouched
   * until Apply) while the CSV export lost it, so the export returned rows the grid never showed.
   */
  const partnerSelectionHydratedRef = useRef(false)
  useEffect(() => {
    const nextIsOrderTypeVisible = selectedPartner?.some((p: any) =>
      /^\d{5}$/.test(p.label?.substring(0, 5)),
    )

    if (partnerSelectionHydratedRef.current) {
      setSelectedOrderType(nextIsOrderTypeVisible ? selectedOrderType : [])
    }
    partnerSelectionHydratedRef.current = true

    setIsOrderTypeVisible(nextIsOrderTypeVisible)
  }, [selectedPartner])

  /** Handling Click Status */
  const handleOverlayClick = (event: any) => {
    if (onOverlayClick) {
      event.stopPropagation()
      onOverlayClick()
    }
  }

  const handleOnCloseClick = (event: any) => {
    if (onCloseClick) {
      event.stopPropagation()
      onCloseClick()
    }
  }

  const disableOverlayClick = (event: any) => {
    event.stopPropagation()
  }

  const handleApplyFilter = (event: any) => {
    setPageNumber(1)
    event.stopPropagation()

    setPartnerOrderVariables({
      ...partnerOrderVariables,
      partner: selectedPartner,
      Search: searchKeyword,
      status: selectedStatus?.map((obj: any) => obj.value),
      /** Same formatter the export uses, so grid and CSV always filter the same window (BDL20-3126). */
      from: formatPartnerOrderDateBound(startDate, false),
      to: formatPartnerOrderDateBound(endDate, true),
      isInternational,
      orderType: selectedOrderType ?? null,
      partnerCodes: selectedPartner.map((p: any) => p.label.split(' - ')[0]),
      partnerOrderTypes: selectedOrderType.map((o: any) => o.label),
    })
    if (onCloseClick) {
      onCloseClick()
    }
  }

  const onResetClick = () => {
    setPageNumber(1)
    setPartnerOrderVariables({
      ...partnerOrderVariables,
      Search: '',
      partner: '',
      partnerCodes: [],
      partnerOrderTypes: [],
      status: ['ALL'],
      selectedOrderType: [],
      orderType: null,
      from: formatPartnerOrderDateBound(startDateValue, false),
      to: formatPartnerOrderDateBound(endDateValue, true),
      isInternational: false,
    })
    if (onCloseClick) {
      onCloseClick()
    }
  }

  /** Handling filtering orders Export as Csv */
  const handleExportCSV = () => {
    /**
     * Enforce the backend's 6-month export limit up front: keep the modal open,
     * highlight the date range field and surface the failure toast, instead of
     * closing the modal and letting the request fail server-side (BDL20-2286).
     */
    if (!isWithinMaxMonths(startDate, endDate)) {
      setDateRangeError(DATE_RANGE_ERROR_MESSAGE)
      if (exportToastIdRef.current) {
        toast.dismiss(exportToastIdRef.current)
      }
      exportToastIdRef.current = toast(
        <ExportToastBody status='error' message={DATE_RANGE_ERROR_MESSAGE} />,
        {
          position: 'top-right',
          type: 'error',
          autoClose: 5000,
          closeOnClick: true,
          closeButton: true,
          draggable: true,
          icon: false,
          className: 'toast--export',
        },
      )
      return
    }
    setDateRangeError(null)

    /** Close the popup immediately on export. */
    if (onCloseClick) {
      onCloseClick()
    }

    /**
     * Show a persistent in-progress toast that stays until the streamed CSV is
     * fully downloaded (BDL20-2286). Replace any previous export toast first.
     */
    if (exportToastIdRef.current) {
      toast.dismiss(exportToastIdRef.current)
    }
    const toastId = toast(<ExportToastBody status='progress' />, {
      position: 'top-right',
      autoClose: false,
      closeOnClick: false,
      closeButton: true,
      draggable: false,
      icon: false,
      className: 'toast--export',
    })
    exportToastIdRef.current = toastId

    /**
     * BDL20-3126: send exactly the filter set the order-list grid sends (see handleApplyFilter) —
     * including partnerCodes and partnerOrderTypes, which are how the Dashboard tiles filter the list.
     * Previously the export sent only PartnerIds/OrderType, so a tile's partner and order-type filters
     * were dropped and the CSV contained rows the grid never displayed. Dates go through the shared
     * bound formatter for the same reason.
     */
    getOrdersExportCSV(toastId, {
      searchKeyword,
      status: selectedStatus?.map((obj: any) => obj.value),
      pId: selectedPartner?.map((obj: any) => obj.value),
      starDate: formatPartnerOrderDateBound(startDate, false),
      endDate: formatPartnerOrderDateBound(endDate, true),
      isInternational,
      selectedOrderType: selectedOrderType?.map((obj: any) => obj.label),
      partnerCodes: selectedPartner?.map(
        (p: any) => p.label.split(' - ')[0],
      ),
      partnerOrderTypes: selectedOrderType?.map((o: any) => o.label),
    })
  }

  /**
   * Process the orders Export as CSV.
   *
   * BDL20-2286: uses mutateAsync (not mutate(vars, {onSuccess, onError})) deliberately.
   * handleExportCSV closes (unmounts) this modal right before this runs. react-query v3's
   * mutate()-level onSuccess/onError are delivered via the MutationObserver owned by this
   * component instance; once the component unmounts, that observer is removed from the
   * mutation's observer list and the callbacks are silently dropped even though the request
   * succeeds. mutateAsync returns the underlying execute() promise directly, which resolves
   * independently of the observer/component lifecycle, so the download always fires.
   *
   * toastId is captured as a parameter (not read from exportToastIdRef.current at resolution
   * time) so a rapid second export click — which overwrites the ref with a new toast id before
   * the first request settles — can never cross-wire the first request's result onto the
   * second request's toast.
   */
  async function getOrdersExportCSV(toastId: any, exportVariables: any) {
    try {
      const response: any = await getOrdersExportCSVMutateAsync(exportVariables)

      /** The streamed CSV arrives as a Blob; save it to the browser. */
      const blob: Blob = response?.data
      const filename =
        getFilenameFromContentDisposition(
          response?.headers?.['content-disposition'],
        ) ?? 'partnerOrders.csv'
      downloadFile(blob, filename)

      toast.update(toastId, {
        render: <ExportToastBody status='success' />,
        type: 'success',
        autoClose: 3000,
        closeOnClick: true,
        closeButton: true,
        draggable: true,
        icon: false,
        className: '',
      })
    } catch (error: any) {
      const detail = await readBlobError(error)

      /** Detailed error to the browser console for debugging. */
      // eslint-disable-next-line no-console
      console.error('CSV export failed', {
        status: error?.response?.status,
        message: detail,
        error,
      })

      toast.update(toastId, {
        render: <ExportToastBody status='error' message={detail} />,
        type: 'error',
        autoClose: 5000,
        closeOnClick: true,
        closeButton: true,
        draggable: true,
        icon: false,
        className: '',
      })
    }
  }

  /** API Call for filtering orders export as CSV using react-query */
  const { mutateAsync: getOrdersExportCSVMutateAsync } = useGetOrdersExportCSV()

  const PopUpAlertClasses = !isCustomStyles
    ? CN(
        'PopUpAlertClasses fixed bg-black bg-opacity-50 w-full h-full z-40 top-0 right-0 bottom-0 left-0 flex items-center justify-center',
        className,
        {
          hidden: !isActive,
        },
      )
    : className

  useEffect(() => {
    const selectedStatuses: any = partnerOrderVariables.status

    const updatedState = selectedStatuses?.map((item: any) => ({
      label: list?.find((obj: any) => obj?.value === item)?.label,
      value: item,
    }))
    setSelectedStatus(updatedState)
  }, [partnerOrderVariables.status])

  useEffect(() => {
    if (selectedStatus?.length > 0) {
      const updatedArray = list.map((item, idx) => {
        if (selectedStatus?.length > 0 && selectedStatus[0]?.value !== 'ALL') {
          return {
            ...item,
            disabled: item.value === 'ALL',
          }
        } else {
          return {
            ...item,
            disabled: item.value !== selectedStatus[idx]?.value,
          }
        }
      })

      setStatusList(updatedArray)
    } else {
      setStatusList(list)
    }
  }, [selectedStatus])

  return (
    <div
      className={PopUpAlertClasses}
      onClick={(e: any) => handleOverlayClick(e)}>
      <div
        className='flex w-fit bg-white rounded-md flex-col items-center'
        onClick={(e: any) => disableOverlayClick(e)}>
        <div className='flex items-center justify-between w-full px-4 py-3 border-b border-b-N-200'>
          <h2 className='text-lg font-bold'>Order Filter</h2>
          <button
            onClick={(e: any) => {
              handleOnCloseClick(e)
            }}
            className='bg-N-200 h-6 w-6 rounded-md hover:bg-N-300 accent-N-400'>
            <i className='ri-close-line'></i>
          </button>
        </div>
        <div className='flex w-full pt-6 px-4 max-w-[580px]'>
          <div className='flex w-full flex-col'>
            <div className='flex w-full flex-col'>
              <p className='text-sm font-medium text-N-700 mb-1'>Status</p>

              <MultiSelect
                value={selectedStatus}
                onChange={(e: any) => {
                  setSelectedStatus(e)
                }}
                options={statusList}
                isMulti={true}
                placeholder='Select Status'
              />
            </div>

            <div className='flex flex-col pt-10 w-full'>
              <p className='text-sm font-medium text-N-700 mb-1'>
                Partner Code and Name
              </p>
              <MultiSelect
                options={partnerList}
                value={selectedPartner}
                onChange={(e: any) => {
                  setSelectedPartner(e)
                  setSelectedOrderType([])
                }}
                isMulti={true}
              />
            </div>
            {isOrderTypeVisible && (
              <div className='flex flex-col pt-10 w-full'>
                <p className='text-sm font-medium text-N-700 mb-1'>
                  Order Type
                </p>
                <MultiSelect
                  options={orderTypeList}
                  value={selectedOrderType}
                  onChange={(e: any) => {
                    setSelectedOrderType(e)
                  }}
                  isMulti={true}
                />
              </div>
            )}
            <div className='flex flex-col pt-10 w-full'>
              <p
                className={CN('text-sm font-medium', {
                  'text-N-700': !dateRangeError,
                  'text-R-500': dateRangeError,
                })}>
                Select Date
              </p>
              <div
                className={CN({
                  'date-range-picker--error': dateRangeError,
                })}>
                <DateRangePickerComponent
                  format='dd/MM/yyyy'
                  delayUpdate={true}
                  placeholder='Select a range'
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(e: any) => {
                    setStartDate(e.target.startDate)
                    const end = new Date(e.target.endDate)
                    setEndDate(end)
                    setDateRangeError(null)
                  }}
                />
              </div>
              {dateRangeError && (
                <span className='text-xs text-R-500 mt-1'>
                  {dateRangeError}
                </span>
              )}
            </div>

            <div className='flex gap-x-2 mt-6'>
              <CheckBox
                checked={isInternational}
                onChange={(e: any) => {
                  setIsInternational(e.target.checked)
                }}
              />

              <p className='text-sm font-medium text-N-700'>
                International orders only
              </p>
            </div>
          </div>
        </div>
        <div className='w-full border-t border-t-N-200 mt-[116px] px-5 py-4'>
          <Button
            className='w-[152px]'
            onClick={(e) => handleApplyFilter(e)}
            disabled={selectedStatus?.length == 0 ? true : false}
            isLoading={false}>
            Apply
          </Button>
          <Button
            className='ml-5 w-[152px]'
            appearance='dull'
            disabled={false}
            onClick={onCloseClick}>
            Close
          </Button>
          <Button
            className='ml-5 w-[152px]'
            appearance='dull'
            disabled={false}
            onClick={onResetClick}>
            Reset Filters
          </Button>
          <Button
            className='ml-5 w-[152px]'
            appearance='dull'
            disabled={false}
            onClick={handleExportCSV}>
            Export CSV
          </Button>
        </div>
      </div>
    </div>
  )
}

AdvanceSearchModal.defaultProps = {
  className: undefined,
  isActive: undefined,
  onCloseClick: undefined,
  onOverlayClick: undefined,
}

export default AdvanceSearchModal
